import { IMonthGoal } from 'store/modules/goal/types'
import { buildDefaultUsersWorking } from 'utils/goalSellerSelection'

import { getSundaysInMonth } from './service'
import { BulkGoalProfile, BulkGoalProfileTemplate, BulkStore, StoreGoalConfig } from './types'

export const compareStoreNames = (firstName: string, secondName: string) =>
  firstName.localeCompare(secondName, 'pt-BR', { sensitivity: 'base' })

export const buildMonthGoalTemplates = (monthGoals: IMonthGoal[] = []): BulkGoalProfileTemplate[] => {
  const firstGoalValue = Number(monthGoals[0]?.value)
  if (!Number.isFinite(firstGoalValue) || firstGoalValue <= 0) return []

  const templates = monthGoals.map((goal, index) => ({
    _id: goal._id || `bulk-month-goal-${index}`,
    bonus: goal.bonus || 0,
    bonusPercentage: !goal.bonus && goal.bonusPercentage === false ? 'no-bonus' : goal.bonusPercentage,
    commission: goal.commission || 0,
    multiplier: index === 0 ? 1 : Number(goal.value) / firstGoalValue,
    name: goal.name || `${index + 1}a meta`,
    type: goal.type || 'user',
    typeBonus: goal.typeBonus || 'user'
  }))

  return templates.every(template => Number.isFinite(template.multiplier) && template.multiplier >= 0)
    ? templates
    : []
}

export const deriveMonthGoals = (
  templates: readonly BulkGoalProfileTemplate[],
  total: number | null,
  selectedSellersCount: number
): BulkGoalProfile[] => {
  const numericTotal = Number(total || 0)
  const canCalculate = Number.isFinite(numericTotal) && numericTotal > 0 && selectedSellersCount > 0
  const mainSellerGoalValue = canCalculate ? numericTotal / selectedSellersCount : 0

  return templates.map(({ multiplier, ...template }) => ({
    ...template,
    value: Number((mainSellerGoalValue * multiplier).toFixed(2))
  }))
}

export const getDerivedMonthGoals = (
  config: Pick<StoreGoalConfig, 'monthGoalTemplates' | 'total' | 'usersNotWorking' | 'usersWorking'>
) => deriveMonthGoals(
  config.monthGoalTemplates,
  config.total,
  [...config.usersWorking, ...config.usersNotWorking].filter(user => user.active).length
)

export const buildEmptyStoreGoalConfig = (
  store: BulkStore,
  selectedMonth: string
): StoreGoalConfig => {
  const dsr = selectedMonth ? getSundaysInMonth(selectedMonth) : 0

  return {
    directCommission: null,
    distribute: null,
    dsr,
    fixedCommission: 0,
    hasFixedCommission: false,
    inheritedSalaryComplement: false,
    monthGoalTemplates: [],
    periodType: null,
    price: null,
    salaryBase: null,
    sections: [],
    sellerGoalDataLoaded: false,
    splitCommission: null,
    storeId: store._id,
    storeName: store.name,
    storeSold: null,
    ticket: null,
    total: null,
    usersNotWorking: [],
    usersWorking: Array.isArray(store.users) ? buildDefaultUsersWorking(store, dsr) : []
  }
}

export { buildDefaultUsersWorking }

export const updateStoreTotal = (
  config: StoreGoalConfig,
  value: number | null
): StoreGoalConfig => ({ ...config, total: value })

export const getUsersAccWeight = (usersWorking: StoreGoalConfig['usersWorking']) =>
  usersWorking.reduce((acc, user) => acc + user.goalWeight, 0) * 100

export const hasValidUsersSelection = (config: StoreGoalConfig) => {
  const activeUsers = config.usersWorking.filter(user => user.active)
  if (activeUsers.length === 0) return false
  if (!config.distribute) return true

  return getUsersAccWeight(activeUsers).toFixed(0) === (activeUsers.length * 100).toFixed(0)
}

export const isStoreCommissionFilled = (config: StoreGoalConfig) =>
  config.directCommission !== null &&
  config.periodType !== null &&
  (config.periodType === 'single' || config.splitCommission !== null) &&
  config.distribute !== null &&
  config.storeSold !== null

const hasValidRequiredValue = (value: number | null) => value !== null && value !== undefined && !(value <= 0)

export const isStoreValuesFilled = (config: StoreGoalConfig) =>
  hasValidRequiredValue(config.salaryBase) &&
  config.dsr !== null &&
  hasValidRequiredValue(config.total) &&
  hasValidRequiredValue(config.ticket) &&
  hasValidRequiredValue(config.price)

export const isStoreGoalsFilled = (config: StoreGoalConfig) => {
  const monthGoals = getDerivedMonthGoals(config)
  return config.monthGoalTemplates.length > 0 &&
    monthGoals.length === config.monthGoalTemplates.length &&
    monthGoals[0].value > 0 &&
    monthGoals.every(goal => Number.isFinite(goal.value) && goal.value >= 0) &&
    hasValidUsersSelection(config)
}
